/**
 * ShareStudioSheet — premium sharing surface.
 * Single preview area, compact settings, carousel support, hidden link UI.
 *
 * Desktop (≥ lg): two-column layout — controls left, live preview right.
 * Mobile (< lg): vertical focused sheet (unchanged).
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Instagram,
  Globe,
  Link2,
  Download,
  Copy,
  Check,
  Zap,
  Image as ImageIcon,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { buildCopyReadyPost, formatPriceSek } from '@/lib/socialPostCopy';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  FarmlySheet,
  FarmlySheetHeader,
  FarmlySheetBody,
} from '@/components/ui/FarmlySheet';
import { SheetTitle, SheetDescription } from '@/components/ui/sheet';

import type {
  ShareTarget,
  SharePlatform,
  ShareFormat,
  TemplateKey,
  ShareAssetType,
} from './ShareTarget';
import { SHARE_FORMAT_SPECS } from './ShareTarget';
import { buildShareUtmUrl } from './shareUtm';
import { generateCaption, type CaptionVariant } from './captions';
import { useShareAssets, downloadDataUrl, openInNewTab, type CarouselResult, type GenerateResult } from './useShareAssets';
import { getTemplatesForFormat } from './templates/registry';
import { ShareStudioShell } from './ShareStudioShell';
import { logShareSession } from './analytics/logShareSession';
import { sendToEditorialDraft } from './editorial/sendToEditorialDraft';

interface ShareStudioSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ShareTarget;
}

const PLATFORM_OPTIONS: { value: SharePlatform; label: string; icon: typeof Instagram; defaultFormat: ShareFormat }[] = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, defaultFormat: 'ig-post' },
  { value: 'facebook', label: 'Facebook', icon: Globe, defaultFormat: 'fb' },
  { value: 'bio', label: 'Länk i bio', icon: Link2, defaultFormat: 'ig-post' },
];

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

type AssetTypeOption = { value: ShareAssetType; format: ShareFormat; label: string };

function formatDateTimeText(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('sv-SE', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function ShareStudioSheet({ open, onOpenChange, target }: ShareStudioSheetProps) {
  const [platform, setPlatform] = useState<SharePlatform>('instagram');
  const [assetType, setAssetType] = useState<ShareAssetType>('single');
  const [selectedFormat, setSelectedFormat] = useState<ShareFormat>('ig-post');
  const [templateKey, setTemplateKey] = useState<TemplateKey>('t1');
  const [captionVariant, setCaptionVariant] = useState<CaptionVariant>('short');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [carouselSlides, setCarouselSlides] = useState<CarouselResult['slides'] | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [heroFallbackUsed, setHeroFallbackUsed] = useState(false);
  const [linkExpanded, setLinkExpanded] = useState(false);
  const [quickShareDone, setQuickShareDone] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [previewState, setPreviewState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retryCount, setRetryCount] = useState(0);
  const [isSendingToEditorial, setIsSendingToEditorial] = useState(false);
  const [editorialOptInDone, setEditorialOptInDone] = useState(false);
  const { generate, generateCarousel, generating } = useShareAssets();

  const isPreviewReady = previewState === 'ready';
  const isPreviewError = previewState === 'error';

  // Track open
  useEffect(() => {
    if (open) {
      track('share_studio_opened', { target_type: target.type, target_id: target.id, platform });
      setQuickShareDone(false);
      setLinkExpanded(false);
      setCustomizeOpen(false);
    }
  }, [open]);

  // Asset type options per platform
  const assetTypeOptions = useMemo((): AssetTypeOption[] => {
    if (platform === 'instagram') {
      return [
        { value: 'single', format: 'ig-post', label: 'Post' },
        { value: 'single', format: 'ig-story', label: 'Story' },
        { value: 'carousel', format: 'ig-post', label: 'Carousel (3)' },
      ];
    }
    if (platform === 'facebook') {
      return [{ value: 'single', format: 'fb', label: 'Facebook' }];
    }
    // bio
    return [{ value: 'single', format: 'ig-post', label: 'Post' }];
  }, [platform]);

  // Derive a combined key for the selected asset option
  const assetOptionKey = assetType === 'carousel' ? 'carousel' : selectedFormat;

  // Available templates for current format (only for single)
  const templates = useMemo(() => getTemplatesForFormat(selectedFormat), [selectedFormat]);

  // Ensure templateKey is valid
  useEffect(() => {
    if (assetType === 'carousel') return;
    if (!templates.find((t) => t.key === templateKey)) {
      setTemplateKey(templates[0]?.key || 't1');
    }
  }, [templates, templateKey, assetType]);

  // Smart defaults when platform changes
  const handlePlatformChange = useCallback((v: string) => {
    if (!v) return;
    const p = v as SharePlatform;
    setPlatform(p);
    if (p === 'instagram') {
      setSelectedFormat('ig-post');
      setAssetType('single');
      setTemplateKey('t1');
    } else if (p === 'facebook') {
      setSelectedFormat('fb');
      setAssetType('single');
      setTemplateKey('t2');
    } else {
      setSelectedFormat('ig-post');
      setAssetType('single');
      setTemplateKey('t1');
    }
  }, []);

  // Handle asset type tab change
  const handleAssetOptionChange = useCallback((key: string) => {
    if (key === 'carousel') {
      setAssetType('carousel');
      setSelectedFormat('ig-post');
      setActiveSlide(0);
    } else {
      setAssetType('single');
      setSelectedFormat(key as ShareFormat);
      // Smart template defaults
      if (key === 'ig-story') setTemplateKey('t3');
      else if (key === 'fb') setTemplateKey('t2');
      else setTemplateKey('t1');
    }
  }, []);

  // Auto-generate preview with retry
  useEffect(() => {
    if (!open) return;
    setPreviewUrl(null);
    setCarouselSlides(null);
    setHeroFallbackUsed(false);
    setActiveSlide(0);
    setPreviewState('loading');
    let cancelled = false;

    const doGenerate = async () => {
      let result: GenerateResult | null = null;
      let carouselResult: CarouselResult | null = null;

      if (assetType === 'carousel') {
        carouselResult = await generateCarousel(target);
      } else {
        result = await generate(target, selectedFormat, templateKey);
      }

      if (cancelled) return;

      if (assetType === 'carousel' && carouselResult) {
        setCarouselSlides(carouselResult.slides);
        setPreviewUrl(carouselResult.slides[0]?.dataUrl ?? null);
        if (carouselResult.retryWithoutHero) setHeroFallbackUsed(true);
        setPreviewState('ready');
      } else if (assetType !== 'carousel' && result) {
        setPreviewUrl(result.dataUrl);
        if (result.retryWithoutHero) setHeroFallbackUsed(true);
        setPreviewState('ready');
      } else {
        // Failed — auto-retry once after 1.5s
        if (retryCount === 0) {
          setRetryCount(1);
          setTimeout(() => {
            if (!cancelled) doGenerate();
          }, 1500);
        } else {
          setPreviewState('error');
        }
      }
    };

    doGenerate();

    return () => { cancelled = true; };
  }, [open, selectedFormat, templateKey, assetType, target.id, target.heroImageUrl, retryCount]);

  // Reset retry count when settings change
  useEffect(() => {
    setRetryCount(0);
  }, [selectedFormat, templateKey, assetType]);

  // Manual retry handler
  const handleManualRetry = useCallback(() => {
    setRetryCount(0);
    setPreviewState('loading');
  }, []);

  // Slide navigation
  const handleSlideChange = useCallback((idx: number) => {
    setActiveSlide(idx);
    if (carouselSlides?.[idx]) {
      setPreviewUrl(carouselSlides[idx].dataUrl);
    }
  }, [carouselSlides]);

  const utmUrl = buildShareUtmUrl(target, platform, {
    format: selectedFormat,
    templateKey: assetType === 'carousel' ? undefined : templateKey,
  });
  const caption = generateCaption(target, platform, captionVariant);

  const dateTimeText = useMemo(() => {
    if (target.type !== 'activity') return null;
    return formatDateTimeText(target.startDate);
  }, [target]);

  const priceText = useMemo(() => {
    if (target.type === 'activity') {
      return target.isFree ? 'Gratis' : formatPriceSek(target.priceOre);
    }
    if (target.type === 'product') {
      return formatPriceSek(target.priceOre);
    }
    return null;
  }, [target]);

  const copyReadyPostText = useMemo(() => {
    return buildCopyReadyPost({
      title: target.title,
      dateTimeText,
      priceText,
      body: caption,
      link: utmUrl,
    });
  }, [target.title, dateTimeText, priceText, caption, utmUrl]);

  // Preview aspect ratio
  const currentSpec = SHARE_FORMAT_SPECS[selectedFormat];

  // -- Actions --

  const handleDownloadSingle = useCallback(async () => {
    if (!previewUrl) return;
    const filename = `farmly-${target.type}-${target.id}-${selectedFormat}-${templateKey}.png`;
    const ok = downloadDataUrl(previewUrl, filename);
    if (!ok) openInNewTab(previewUrl);
    toast.success(ok ? 'Bild nedladdad!' : 'Bilden öppnades i en ny flik.');
    track('share_studio_asset_downloaded', {
      target_type: target.type, target_id: target.id,
      format: selectedFormat, template_key: templateKey,
    });
  }, [previewUrl, selectedFormat, templateKey, target]);

  const handleDownloadAll = useCallback(async () => {
    if (!carouselSlides) return;
    let allOk = true;
    for (const slide of carouselSlides) {
      const filename = `farmly-${target.type}-${target.id}-carousel-${slide.index + 1}.png`;
      const ok = downloadDataUrl(slide.dataUrl, filename);
      if (!ok) { openInNewTab(slide.dataUrl); allOk = false; }
      await new Promise((r) => setTimeout(r, 200));
    }
    toast.success(allOk ? '3 bilder nedladdade!' : 'Bilderna öppnades — spara dem därifrån.');
    track('share_studio_asset_downloaded', {
      target_type: target.type, target_id: target.id,
      format: 'ig-post', template_key: 'carousel',
    });
  }, [carouselSlides, target]);

  const handleCopyPost = useCallback(async () => {
    const ok = await copyText(copyReadyPostText);
    if (ok) {
      setCopiedField('post');
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Inlägget kopierat — klistra in i Facebook eller Instagram.');
      track('share_studio_caption_copied', {
        target_type: target.type, target_id: target.id, platform, variant: captionVariant,
      });
    } else {
      toast.error('Kunde inte kopiera inlägget.');
    }
  }, [copyReadyPostText, target, platform, captionVariant]);

  const handleCopyLink = useCallback(async () => {
    const ok = await copyText(utmUrl);
    if (ok) {
      setCopiedField('link');
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Länk kopierad!');
      track('share_studio_link_copied', {
        target_type: target.type, target_id: target.id, platform,
      });
    } else {
      toast.error('Kunde inte kopiera.');
    }
  }, [utmUrl, target, platform]);

  const handleQuickShare = useCallback(async () => {
    const results: string[] = [];
    const isCarousel = assetType === 'carousel' && carouselSlides;

    // Download image(s)
    if (isCarousel && carouselSlides) {
      let allOk = true;
      for (const slide of carouselSlides) {
        const filename = `farmly-${target.type}-${target.id}-carousel-${slide.index + 1}.png`;
        const ok = downloadDataUrl(slide.dataUrl, filename);
        if (!ok) { openInNewTab(slide.dataUrl); allOk = false; }
        await new Promise((r) => setTimeout(r, 200));
      }
      results.push(allOk ? '3 bilder sparade' : '3 bilder öppnade');
    } else if (previewUrl) {
      const filename = `farmly-${target.type}-${target.id}-${selectedFormat}-${templateKey}.png`;
      const ok = downloadDataUrl(previewUrl, filename);
      results.push(ok ? 'Bild sparad' : 'Bild öppnad');
    }

    // Copy post text (includes title/date/price/body/link)
    const postOk = await copyText(copyReadyPostText);
    if (postOk) results.push('Inlägg kopierat');

    track('share_studio_quick_action', {
      target_type: target.type, target_id: target.id,
      platform, format: selectedFormat,
      template_key: assetType === 'carousel' ? 'carousel' : templateKey,
    });

    // Non-blocking analytics log
    if (target.producerId) {
      logShareSession({
        producer_id: target.producerId,
        entity_type: target.type,
        entity_id: target.id,
        template_key: assetType === 'carousel' ? 'carousel' : templateKey,
        platform,
        caption_length: captionVariant,
      });
    }

    if (results.length > 0) {
      toast.success('Klart! 🎉 Bilden är sparad och texten kopierad.', { duration: 3500 });
      setQuickShareDone(true);
    } else {
      toast.error('Något gick fel. Prova kopiera manuellt.');
    }
  }, [previewUrl, carouselSlides, copyReadyPostText, target, platform, selectedFormat, templateKey, assetType]);

  const handleSendToEditorial = useCallback(async () => {
    if (isSendingToEditorial) return;
    if (target.type !== 'activity') {
      toast.error('Kunde inte spara tillstånd för Farmly-delning');
      return;
    }

    setIsSendingToEditorial(true);
    try {
      const result = await sendToEditorialDraft({
        target,
        caption,
        shareUrl: utmUrl,
        surface: 'sheet',
      });

      if (!result.ok) {
        throw new Error(result.code || 'create_social_post_draft_failed');
      }

      setEditorialOptInDone(true);
      toast.success('Tack — Farmly får också dela detta', {
        description: 'Innehållet finns nu i Farmlys redaktionella arbetsflöde.',
      });
    } catch {
      toast.error('Kunde inte spara tillstånd för Farmly-delning');
    } finally {
      setIsSendingToEditorial(false);
    }
  }, [caption, utmUrl, target, isSendingToEditorial]);

  /* ─── Shared preview block ─── */
  const previewBlock = (compact?: boolean) => {
    const maxW = compact ? 'max-w-[200px]' : '';
    return (
      <div className={`rounded-xl border border-border overflow-hidden bg-muted/20 ${maxW} ${compact ? 'mx-auto' : ''}`}>
        {previewState === 'loading' || generating ? (
          <div className="w-full relative flex flex-col items-center justify-center gap-2" style={{ aspectRatio: `${currentSpec.w} / ${currentSpec.h}` }}>
            <Skeleton className="absolute inset-0 w-full h-full" />
            <div className="relative z-10 flex flex-col items-center gap-1.5">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              {!compact && <p className="text-[11px] text-muted-foreground">Förbereder ditt inlägg…</p>}
            </div>
          </div>
        ) : isPreviewError ? (
          <div className="w-full flex flex-col items-center justify-center gap-2" style={{ aspectRatio: `${currentSpec.w} / ${currentSpec.h}` }}>
            <ImageIcon className="h-6 w-6 text-muted-foreground/30" />
            <p className="text-[11px] text-muted-foreground">Bilden kunde inte laddas just nu.</p>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleManualRetry}>
              <RefreshCw className="h-3 w-3" />
              Försök igen
            </Button>
          </div>
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt="Share preview"
            className={`w-full h-auto block transition-transform ${isPreviewReady ? 'cursor-pointer active:scale-[0.98] hover:brightness-[0.97]' : ''}`}
            style={{ aspectRatio: `${currentSpec.w} / ${currentSpec.h}`, objectFit: 'contain' }}
            onClick={isPreviewReady ? handleQuickShare : undefined}
            role={isPreviewReady ? 'button' : undefined}
            tabIndex={isPreviewReady ? 0 : undefined}
            aria-label={isPreviewReady ? 'Tryck för att dela' : undefined}
          />
        ) : (
          <div className="w-full flex items-center justify-center text-muted-foreground" style={{ aspectRatio: `${currentSpec.w} / ${currentSpec.h}` }}>
            <ImageIcon className={`${compact ? 'h-6 w-6' : 'h-8 w-8'} opacity-20`} />
          </div>
        )}
        {assetType === 'carousel' && carouselSlides && (
          <div className="flex justify-center gap-2 py-2 bg-muted/30 border-t border-border">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                onClick={() => handleSlideChange(i)}
                className={`${compact ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'} rounded-full font-semibold transition-colors ${
                  activeSlide === i
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ─── Quick Share card (shared between mobile & desktop) ─── */
  const quickShareCard = (
    <div className="rounded-xl border border-border bg-muted/10 px-3 py-3 space-y-2.5">
      {!quickShareDone ? (
        <>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Dela detta inlägg</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Det här underlaget är till för att du enkelt ska kunna dela aktiviteten själv.
          </p>
          <p className="text-xs text-muted-foreground">
            Bilden sparas på din telefon. Text och länk kopieras så att du kan klistra in manuellt i Instagram eller Facebook.
          </p>
          <Button className="w-full gap-2" onClick={handleQuickShare} disabled={!isPreviewReady || generating}>
            <Zap className="h-4 w-4" />
            {previewState === 'loading' || generating ? 'Förbereder…' : 'Dela detta inlägg'}
          </Button>
        </>
      ) : (
        <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-3.5 w-3.5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Klart! 🎉</p>
              <p className="text-xs text-muted-foreground">Bilden är sparad och texten kopierad.</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground font-medium">Öppna manuellt</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5 text-xs"
                onClick={() => {
                  track('share_studio_open_app', { app: 'instagram', target_type: target.type, target_id: target.id });
                  window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
                }}
              >
                <Instagram className="h-3.5 w-3.5" />
                Öppna Instagram
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5 text-xs"
                onClick={() => {
                  track('share_studio_open_app', { app: 'facebook', target_type: target.type, target_id: target.id });
                  window.open('https://www.facebook.com/', '_blank', 'noopener,noreferrer');
                }}
              >
                <Globe className="h-3.5 w-3.5" />
                Öppna Facebook
              </Button>
            </div>
          </div>
          {/* Farmly opt-in */}
          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-[11px] text-muted-foreground font-medium">Låt Farmly också dela</p>
            <p className="text-[11px] text-muted-foreground">
              Om du vill kan Farmly också lyfta den här aktiviteten i våra egna kanaler.
            </p>
            {editorialOptInDone ? (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-primary" />
                Farmly delar också detta
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <Switch
                  id="share-sheet-editorial-opt-in"
                  checked={false}
                  onCheckedChange={(checked) => {
                    if (checked && target.type === 'activity') handleSendToEditorial();
                  }}
                  disabled={isSendingToEditorial || target.type !== 'activity'}
                />
                <label
                  htmlFor="share-sheet-editorial-opt-in"
                  className="text-xs text-foreground cursor-pointer select-none"
                >
                  {isSendingToEditorial ? 'Sparar…' : 'Farmly får också dela detta'}
                </label>
              </div>
            )}
          </div>

          <button
            onClick={() => onOpenChange(false)}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors w-full text-center"
          >
            Stäng — jag klistrar in själv
          </button>
        </div>
      )}
    </div>
  );

  /* ─── Customize controls (shared) ─── */
  const customizeControls = (
    <div className="space-y-5 animate-in fade-in duration-150">
      {/* Appearance settings */}
      <div className="rounded-lg border border-border bg-muted/5 p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Utseende</p>

        <div className="flex items-center gap-3">
          <Label className="text-xs text-muted-foreground font-medium w-20 shrink-0">Plattform</Label>
          <ToggleGroup type="single" value={platform} onValueChange={handlePlatformChange} className="justify-start flex-1">
            {PLATFORM_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} aria-label={opt.label} className="gap-1 text-xs px-2.5 h-8">
                <opt.icon className="h-3.5 w-3.5" />
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        {assetTypeOptions.length > 1 && (
          <div className="flex items-center gap-3">
            <Label className="text-xs text-muted-foreground font-medium w-20 shrink-0">Format</Label>
            <ToggleGroup type="single" value={assetOptionKey} onValueChange={handleAssetOptionChange} className="justify-start flex-1">
              {assetTypeOptions.map((opt) => {
                const key = opt.value === 'carousel' ? 'carousel' : opt.format;
                return (
                  <ToggleGroupItem key={key} value={key} className="text-xs px-2.5 h-8">{opt.label}</ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </div>
        )}

        {assetType === 'single' && templates.length > 1 && (
          <div className="flex items-center gap-3">
            <Label className="text-[11px] text-muted-foreground/70 font-medium w-20 shrink-0">Utseende</Label>
            <ToggleGroup type="single" value={templateKey} onValueChange={(v) => v && setTemplateKey(v as TemplateKey)} className="justify-start flex-1">
              {templates.map((t) => (
                <ToggleGroupItem key={t.key} value={t.key} className="text-xs px-2.5 h-7">{t.label}</ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Label className="text-[11px] text-muted-foreground/70 font-medium w-20 shrink-0">Textlängd</Label>
          <ToggleGroup type="single" value={captionVariant} onValueChange={(v) => v && setCaptionVariant(v as CaptionVariant)} className="justify-start flex-1">
            <ToggleGroupItem value="short" className="text-xs px-2.5 h-7">Kort</ToggleGroupItem>
            <ToggleGroupItem value="long" className="text-xs px-2.5 h-7">Utökad</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Text editor area */}
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-foreground tracking-wide uppercase">Text till inlägget</p>
        <p className="text-[11px] text-muted-foreground">Du kan justera texten innan du delar.</p>
        <Textarea
          value={caption}
          readOnly
          rows={captionVariant === 'long' ? 6 : 3}
          className="text-xs font-mono resize-none bg-muted/20 border-border"
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <p className="w-full text-[11px] text-muted-foreground font-medium">För dig</p>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs flex-1" onClick={assetType === 'carousel' ? handleDownloadAll : handleDownloadSingle} disabled={!isPreviewReady}>
          <Download className="h-3.5 w-3.5" />
          {assetType === 'carousel' ? 'Ladda ner alla' : 'Ladda ner'}
        </Button>
        <Button variant="default" size="sm" className="gap-1.5 text-xs flex-1" onClick={handleCopyPost}>
          {copiedField === 'post' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          Kopiera inlägg
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs flex-1" onClick={handleCopyLink}>
          {copiedField === 'link' ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
          Kopiera länk
        </Button>
        <p className="w-full text-[11px] text-muted-foreground font-medium mt-1">För Farmly</p>
        <p className="w-full text-[11px] text-muted-foreground -mt-1">
          Om du vill kan du också ge Farmly tillåtelse att dela aktiviteten i våra kanaler.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="gap-1.5 text-xs w-full"
          onClick={handleSendToEditorial}
          disabled={isSendingToEditorial || target.type !== 'activity'}
        >
          {isSendingToEditorial ? 'Sparar…' : 'Farmly får också dela detta'}
        </Button>
      </div>

      {/* Link reveal */}
      <button onClick={() => setLinkExpanded(!linkExpanded)} className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
        {linkExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Visa spårad länk
      </button>
      {linkExpanded && (
        <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
          <p className="text-[11px] font-mono text-muted-foreground break-all select-all">{utmUrl}</p>
        </div>
      )}
    </div>
  );

  return (
    <FarmlySheet
      open={open}
      onOpenChange={onOpenChange}
      title="Share Studio"
      className="max-h-[95dvh] lg:max-w-[1100px] lg:mx-auto lg:rounded-2xl lg:max-h-[88dvh]"
    >
      <FarmlySheetHeader>
        {customizeOpen ? (
          <button
            onClick={() => setCustomizeOpen(false)}
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Snabb delning
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-full bg-accent flex items-center justify-center">
              <Zap className="h-3 w-3 text-accent-foreground" />
            </div>
            <SheetTitle className="text-lg font-semibold">Share Studio</SheetTitle>
          </div>
        )}
        <SheetDescription className="text-sm text-muted-foreground mt-0.5">
          {customizeOpen ? 'Anpassa hur inlägget ser ut.' : 'Vi har gjort ett färdigt inlägg åt dig.'}
        </SheetDescription>
      </FarmlySheetHeader>

      <FarmlySheetBody>
        {customizeOpen ? (
          /* ═══════ Customize mode ═══════ */
          <ShareStudioShell
            mobilePreviewPosition="inline"
            controls={
              <div className="space-y-5">
                {/* Mobile-only compact preview */}
                <div className="lg:hidden">
                  {previewBlock(true)}
                </div>
                {customizeControls}
              </div>
            }
            preview={previewBlock()}
          />
        ) : (
          /* ═══════ Quick mode (default) ═══════ */
          <ShareStudioShell
            mobilePreviewPosition="top"
            controls={
              <div className="space-y-4 animate-in fade-in duration-150">
                {heroFallbackUsed && (
                  <p className="text-[11px] text-muted-foreground">
                    ⚠️ Omslagsbild kunde inte användas — variant utan bild skapades.
                  </p>
                )}

                {quickShareCard}

                <button
                  onClick={() => setCustomizeOpen(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                  Vill du ändra något? Anpassa inlägget
                </button>
              </div>
            }
            preview={previewBlock()}
          />
        )}
      </FarmlySheetBody>
    </FarmlySheet>
  );
}
